/* ShelterChat — chat rifugio↔utente per adozioni.
   Route: /app/rifugio/chat/:tid
   TR-clean look. Bottom padding rispetta MobileNav.
*/
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Send, Loader2, MessageCircle, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hapticTap } from "@/lib/haptics";

const POLL_MS = 6000;

export default function ShelterChat() {
  const { tid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/shelter/threads/${tid}/messages`);
      setThread(data.thread);
      setMessages(data.messages || []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Conversazione non trovata");
      nav("/app");
    } finally {
      setLoading(false);
    }
  }, [tid, nav]);

  useEffect(() => {
    load();
    const iv = setInterval(load, POLL_MS);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post(`/shelter/threads/${tid}/messages`, { text: t });
      setText("");
      hapticTap();
      load();
    } catch {
      toast.error("Impossibile inviare");
    } finally { setSending(false); }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-[#FF7A1A]" />
    </div>;
  }
  if (!thread) return null;

  const iAmShelter = thread.shelter_user_id === user?.id;
  const otherName = iAmShelter ? (thread.user_name || "Utente") : (thread.shelter_name || "Rifugio");

  return (
    <div className="flex flex-col" style={{ minHeight: "calc(100vh - 56px)" }} data-testid="shelter-chat-page">
      {/* Header */}
      <div className="sticky top-14 z-30 bg-[#FBF7F1]/95 backdrop-blur border-b border-[#F0EBE3] px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => nav(-1)}
          data-testid="shelter-chat-back"
          className="h-9 w-9 rounded-full bg-white border border-[#F0EBE3] flex items-center justify-center"
          aria-label="Indietro"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] font-semibold inline-flex items-center gap-1.5">
            <HeartHandshake className="h-3 w-3" /> {iAmShelter ? "Richiesta ricevuta" : "Chat rifugio"}
          </div>
          <div className="font-semibold text-[15px] truncate">{otherName}</div>
        </div>
        {!iAmShelter && (
          <Link
            to={`/app/rifugio/${thread.shelter_id}`}
            className="text-[11.5px] font-semibold text-[#FF7A1A] px-3 py-1.5 rounded-full bg-[#FFF6E6] border border-[#F0D9B0]"
          >
            Vedi profilo
          </Link>
        )}
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-2"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 160px)",
          background: "#FBF7F1",
        }}
      >
        {messages.length === 0 && (
          <div className="text-center text-[13px] text-[#9A9A9A] pt-8">
            <MessageCircle className="h-8 w-8 mx-auto text-[#F0D9B0] mb-2" />
            Nessun messaggio ancora.
          </div>
        )}
        {messages.map((m) => {
          const mine = m.from_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className="max-w-[78%] rounded-2xl px-3.5 py-2.5 text-[14px] leading-relaxed"
                style={{
                  background: mine
                    ? "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)"
                    : "white",
                  color: mine ? "white" : "#1A1A1A",
                  border: mine ? "none" : "1px solid #F0EBE3",
                  boxShadow: mine
                    ? "0 6px 14px -6px rgba(255,122,26,0.4)"
                    : "0 2px 8px -4px rgba(0,0,0,0.08)",
                  borderBottomRightRadius: mine ? "6px" : undefined,
                  borderBottomLeftRadius: mine ? undefined : "6px",
                }}
              >
                {m.text}
                <div className={`mt-1 text-[10px] ${mine ? "text-white/70" : "text-[#9A9A9A]"}`}>
                  {new Date(m.created_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer — fixed above the MobileNav */}
      <div
        className="fixed inset-x-0 z-30 border-t border-[#F0EBE3] bg-[#FBF7F1]/95 backdrop-blur px-3 py-3"
        style={{
          bottom: "calc(env(safe-area-inset-bottom, 0px) + 76px)",
        }}
      >
        <div className="max-w-3xl mx-auto flex items-end gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Scrivi un messaggio…"
            rows={1}
            data-testid="shelter-chat-input"
            className="flex-1 resize-none rounded-2xl px-3 py-2.5 text-[14px] bg-white border border-[#F0EBE3] outline-none focus:border-[#FF7A1A] max-h-[120px]"
          />
          <button
            onClick={send}
            disabled={sending || !text.trim()}
            data-testid="shelter-chat-send"
            className="h-11 w-11 rounded-full flex items-center justify-center text-white shrink-0"
            style={{
              background: !text.trim() ? "#E5DECF" : "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
              boxShadow: !text.trim() ? "none" : "0 6px 16px -6px rgba(255,122,26,0.55)",
            }}
            aria-label="Invia"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
